import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { UserListInitialState, UserListLoad, UserListDownloadStart } from './user-list.actions';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { UserListService } from './user-list.service';
import { UserBrief } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NavigateUserService } from '../../../../services/navigate-user.service';

export type UserListStateModel = ListServerPaginationStateModel<UserBrief>;

const initialState: UserListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<UserListStateModel>({
  name: 'userList',
  defaults: initialState,
})
@Injectable()
export class UserListState {
  constructor(
    private userListService: UserListService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: UserListStateModel): UserListStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: UserListStateModel): boolean {
    return state.loaded;
  }

  @Action(UserListLoad)
  getUsers(
    ctx: StateContext<UserListStateModel>,
    { payload }: UserListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userListService.getUserList(payload).pipe(
      take(1),
      map((list: UserBrief[]) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserListInitialState)
  setInitialState(ctx: StateContext<UserListStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(UserListDownloadStart)
  downloadUserList(
    ctx: StateContext<UserListStateModel>,
    { payload: { dialogRef, navigationNeeded, ...body } }: UserListDownloadStart,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userListService.downloadUserList(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.export.success.exportStart');
        dialogRef.close();
        if (navigationNeeded) {
          this.navigateUserService.navigateUserToAbsolutePath('/dashboard/exports');
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
}
